import React, { useState, useEffect } from 'react';
import Layout from '../../Layout/Layout';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { 
  FaPlus, FaSearch, FaFilter, FaSort, 
  FaEdit, FaTrash, FaExclamationTriangle,
  FaSpinner, FaArrowUp, FaArrowDown,
  FaHome, FaCar, FaUtensils, FaShoppingCart, 
  FaPlane, FaGraduationCap, FaGift, FaMoneyBillWave,
  FaPiggyBank, FaChartLine, FaWallet, FaCreditCard
} from 'react-icons/fa';
import './TransactionCategories.css';

// API Configuration
const API_BASE_URL = 'http://localhost:8000/api';
const CATEGORIES_ENDPOINT = '/categories/';

// SweetAlert2 Configuration
const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true
});

const TransactionCategories = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [showFilters, setShowFilters] = useState(false);

  // Icon mapping
  const iconMap = {
    FaHome: FaHome,
    FaCar: FaCar,
    FaUtensils: FaUtensils,
    FaShoppingCart: FaShoppingCart,
    FaPlane: FaPlane,
    FaGraduationCap: FaGraduationCap,
    FaGift: FaGift,
    FaMoneyBillWave: FaMoneyBillWave,
    FaPiggyBank: FaPiggyBank,
    FaChartLine: FaChartLine,
    FaWallet: FaWallet,
    FaCreditCard: FaCreditCard
  };

  // Load categories on component mount
  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter and sort categories when dependencies change
  useEffect(() => {
    let result = [...categories];

    // Apply search filter
    if (searchTerm) {
      result = result.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        category.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(category => category.category_type === filterType);
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortConfig.key === 'name') {
        return sortConfig.direction === 'asc' 
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (sortConfig.key === 'type') {
        return sortConfig.direction === 'asc'
          ? a.category_type.localeCompare(b.category_type)
          : b.category_type.localeCompare(a.category_type);
      }
      return 0;
    });

    setFilteredCategories(result);
  }, [categories, searchTerm, filterType, sortConfig]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const response = await fetch(`${API_BASE_URL}${CATEGORIES_ENDPOINT}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });

      if (response.status === 401) {
        throw new Error('Session expired');
      }

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      if (error.message === 'Session expired') {
        showSessionExpiredAlert();
      } else {
        setError('Failed to load categories. Please try again later.');
        showErrorAlert('Failed to load categories');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    const result = await Swal.fire({
      title: 'Delete Category',
      text: 'Are you sure you want to delete this category? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(`${API_BASE_URL}${CATEGORIES_ENDPOINT}${categoryId}/`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          setCategories(categories.filter(cat => cat.id !== categoryId));
          showSuccessAlert('Category deleted successfully');
        } else {
          throw new Error('Failed to delete category');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        showErrorAlert('Failed to delete category');
      }
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const showSuccessAlert = (message) => {
    Toast.fire({
      icon: 'success',
      title: message
    });
  };

  const showErrorAlert = (message) => {
    Toast.fire({
      icon: 'error',
      title: message
    });
  };

  const showSessionExpiredAlert = () => {
    Swal.fire({
      title: 'Session Expired',
      text: 'Your session has expired. Please log in again.',
      icon: 'warning',
      confirmButtonColor: '#1a237e',
      confirmButtonText: 'Go to Login',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/login');
      }
    });
  };

  const getCategoryTypeLabel = (type) => {
    const types = {
      income: 'Income',
      expense: 'Expense',
      investment: 'Investment',
      savings: 'Savings'
    };
    return types[type] || type;
  };

  const getCategoryTypeColor = (type) => {
    const colors = {
      income: '#4caf50',
      expense: '#f44336',
      investment: '#2196f3',
      savings: '#ff9800'
    };
    return colors[type] || '#757575';
  };

  const renderIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || FaWallet;
    return <IconComponent />;
  };

  if (loading) {
    return (
      <Layout>
        <div className="categories-loading">
          <FaSpinner className="spinner" />
          <p>Loading categories...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="categories-container">
        <div className="categories-header">
          <div className="header-content">
            <h1>Transaction Categories</h1>
            <p>Manage your transaction categories</p>
          </div>
          <button 
            className="create-category-btn"
            onClick={() => navigate('/create-category')}
          >
            <FaPlus /> Create Category
          </button>
        </div>

        <div className="categories-toolbar">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="toolbar-actions">
            <button 
              className="btn-secondary filter-btn"
              onClick={() => setShowFilters(!showFilters)}
            >
              <FaFilter /> Filter
            </button>
            <div className="sort-buttons">
              <button 
                className={`btn-secondary ${sortConfig.key === 'name' ? 'active' : ''}`}
                onClick={() => handleSort('name')}
              >
                <FaSort /> Name
                {sortConfig.key === 'name' && (
                  sortConfig.direction === 'asc' ? <FaArrowUp /> : <FaArrowDown />
                )}
              </button>
            </div>
          </div>
        </div>

        {showFilters && (
          <div className="filter-panel">
            <div className="filter-group">
              <label>Category Type:</label>
              <select 
                value={filterType} 
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
                <option value="investment">Investment</option>
                <option value="savings">Savings</option>
              </select>
            </div>
          </div>
        )}

        {error && (
          <div className="error-banner">
            <FaExclamationTriangle />
            <span>{error}</span>
          </div>
        )}

        {filteredCategories.length === 0 ? (
          <div className="no-categories">
            <p>No categories found</p>
            {searchTerm || filterType !== 'all' ? (
              <button 
                className="btn-secondary"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                }}
              >
                Clear Filters
              </button>
            ) : (
              <button 
                className="btn-primary"
                onClick={() => navigate('/create-category')}
              >
                Create Your First Category
              </button>
            )}
          </div>
        ) : (
          <div className="categories-grid">
            {filteredCategories.map(category => (
              <div 
                key={category.id} 
                className="category-card"
                style={{ borderColor: category.color }}
              >
                <div 
                  className="category-icon"
                  style={{ backgroundColor: category.color }}
                >
                  {renderIcon(category.icon)}
                </div>
                <div className="category-content">
                  <h3>{category.name}</h3>
                  <p className="category-description">{category.description}</p>
                  <span 
                    className="category-type"
                    style={{ backgroundColor: getCategoryTypeColor(category.category_type) }}
                  >
                    {getCategoryTypeLabel(category.category_type)}
                  </span>
                </div>
                <div className="category-actions">
                  <button 
                    className="btn-icon edit-btn"
                    onClick={() => navigate(`/categories/edit/${category.id}`)}
                    title="Edit Category"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    className="btn-icon delete-btn"
                    onClick={() => handleDelete(category.id)}
                    title="Delete Category"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TransactionCategories;
